/**
 * Presentational panels for the Commissioned Book. Team pie and Top
 * Clients are click-to-filter: the selected entry stays vivid, the
 * rest dim, clicking toggles the filter via onSelect.
 */
import { useState } from "react";
import { Timer, Zap, Turtle } from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  Tooltip as RechartsTooltip,
} from "recharts";

export const fmtGbp = (v) => (v == null || Number.isNaN(v) ? "-" : `£${Math.round(v).toLocaleString("en-GB")}`);
export const fmtGbpShort = (v) => {
  if (v == null) return "-";
  if (Math.abs(v) >= 1_000_000) return `£${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `£${Math.round(v / 1_000).toLocaleString("en-GB")}k`;
  return `£${Math.round(v).toLocaleString("en-GB")}`;
};
export const fmtDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
};
const fmtMonth = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
};

const TEAM_COLORS = { UK: "#1B4F8A", International: "#3EA8DC", PSE: "#0E9F6E" };
const EXTRA_COLORS = ["#C084FC", "#F59E0B", "#F43F5E", "#64748B"];
export const teamColor = (team, i = 0) => TEAM_COLORS[team] || EXTRA_COLORS[i % EXTRA_COLORS.length];
const QQ_COLORS = { Qual: "#1B4F8A", Quant: "#3EA8DC", Both: "#0E9F6E", Unspecified: "#A8A29E" };

export function PanelTitle({ children }) {
  return (
    <p className="text-[10px] font-mono tracking-[0.18em] uppercase text-stone-500 mb-3">
      {children}
    </p>
  );
}

export function TeamPie({ byTeam, selected, onSelect, idPrefix = "commissioned" }) {
  const [metric, setMetric] = useState("count");
  const data = byTeam.map((t, i) => ({
    name: t.team, value: metric === "count" ? t.count : t.value,
    count: t.count, gbp: t.value, fill: teamColor(t.team, i),
  }));
  return (
    <div data-testid={`${idPrefix}-team-pie`} className="bg-white rounded-xl border border-stone-200 p-4">
      <div className="flex items-center justify-between mb-1">
        <PanelTitle>Projects per team</PanelTitle>
        <div className="flex rounded-md border border-stone-200 overflow-hidden text-[10px] font-mono uppercase">
          {["count", "value"].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMetric(m)}
              data-testid={`${idPrefix}-team-toggle-${m}`}
              className={`px-2 py-1 transition-colors ${metric === m ? "bg-[#1B4F8A] text-white" : "bg-white text-stone-500 hover:bg-stone-50"}`}
            >
              {m === "count" ? "projects" : "£ value"}
            </button>
          ))}
        </div>
      </div>
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data} dataKey="value" nameKey="name"
              cx="50%" cy="50%" innerRadius={42} outerRadius={70}
              paddingAngle={2} strokeWidth={1} cursor="pointer"
              onClick={(entry) => {
                const n = entry?.name ?? entry?.payload?.name;
                if (n) onSelect(n);
              }}
            >
              {data.map((d) => (
                <Cell
                  key={d.name}
                  fill={d.fill}
                  fillOpacity={selected && d.name !== selected ? 0.25 : 1}
                />
              ))}
            </Pie>
            <RechartsTooltip
              formatter={(v, name, p) =>
                metric === "count"
                  ? [`${v} project${v === 1 ? "" : "s"} · ${fmtGbpShort(p.payload.gbp)}`, name]
                  : [`${fmtGbpShort(v)} · ${p.payload.count} project${p.payload.count === 1 ? "" : "s"}`, name]}
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="mt-2 space-y-0.5" data-testid={`${idPrefix}-team-legend`}>
        {data.map((d) => (
          <li key={d.name}>
            <button
              type="button"
              onClick={() => onSelect(d.name)}
              data-testid={`${idPrefix}-team-legend-${d.name}`}
              title={selected === d.name ? "Clear team filter" : `Drill into ${d.name}`}
              className={`w-full flex items-center gap-2 text-xs text-stone-600 rounded px-1.5 py-1 hover:bg-stone-50 transition-colors ${selected && d.name !== selected ? "opacity-40" : ""}`}
            >
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.fill }} />
              <span className="font-medium text-slate-800">{d.name}</span>
              <span className="ml-auto font-mono tabular-nums">
                {d.count} · {fmtGbpShort(d.gbp)}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <p className="text-[10px] text-stone-400 mt-2 px-1.5">click a team to drill down</p>
    </div>
  );
}

export function QualQuantMix({ mix }) {
  const total = mix.reduce((s, m) => s + m.count, 0) || 1;
  return (
    <div data-testid="commissioned-qualquant" className="bg-white rounded-xl border border-stone-200 p-4">
      <PanelTitle>Qual / Quant mix</PanelTitle>
      <div className="flex h-4 rounded-full overflow-hidden border border-stone-100">
        {mix.map((m) => (
          <div
            key={m.key}
            title={`${m.key}: ${m.count}`}
            style={{ width: `${(m.count / total) * 100}%`, background: QQ_COLORS[m.key] || "#A8A29E" }}
          />
        ))}
      </div>
      <ul className="mt-3 space-y-1">
        {mix.map((m) => (
          <li key={m.key} className="flex items-center gap-2 text-xs text-stone-600">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: QQ_COLORS[m.key] || "#A8A29E" }} />
            {m.key}
            <span className="ml-auto font-mono tabular-nums">
              {m.count} · {Math.round((m.count / total) * 100)}%
            </span>
          </li>
        ))}
        {mix.length === 0 && <li className="text-xs text-stone-400">No projects in this view.</li>}
      </ul>
    </div>
  );
}

export function TimeToWin({ ttw }) {
  return (
    <div data-testid="commissioned-time-to-win" className="bg-white rounded-xl border border-stone-200 p-4">
      <PanelTitle>Time to win · quote → commission</PanelTitle>
      <div className="flex items-end gap-5">
        <div>
          <p className="text-3xl font-medium tabular-nums text-[#1B4F8A] leading-none">
            {ttw.median_days ?? "-"}<span className="text-sm font-normal text-stone-400 ml-1">days</span>
          </p>
          <p className="text-[10px] font-mono uppercase tracking-wider text-stone-400 mt-1">median</p>
        </div>
        <div>
          <p className="text-xl font-medium tabular-nums text-slate-700 leading-none">
            {ttw.avg_days != null ? Math.round(ttw.avg_days) : "-"}<span className="text-xs font-normal text-stone-400 ml-1">days</span>
          </p>
          <p className="text-[10px] font-mono uppercase tracking-wider text-stone-400 mt-1">average</p>
        </div>
      </div>
      <div className="mt-3 space-y-1.5 text-xs">
        {ttw.fastest && (
          <p className="flex items-center gap-1.5 text-stone-600" data-testid="commissioned-ttw-fastest">
            <Zap className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
            <span className="truncate">Fastest: <span className="font-medium text-slate-800">{ttw.fastest.client_company || "-"}</span></span>
            <span className="ml-auto font-mono tabular-nums shrink-0">{ttw.fastest.days_to_win}d</span>
          </p>
        )}
        {ttw.slowest && (
          <p className="flex items-center gap-1.5 text-stone-600" data-testid="commissioned-ttw-slowest">
            <Turtle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
            <span className="truncate">Slowest: <span className="font-medium text-slate-800">{ttw.slowest.client_company || "-"}</span></span>
            <span className="ml-auto font-mono tabular-nums shrink-0">{ttw.slowest.days_to_win}d</span>
          </p>
        )}
        <p className="text-[10px] text-stone-400 pt-1 flex items-center gap-1">
          <Timer className="w-3 h-3" /> based on {ttw.n} project{ttw.n === 1 ? "" : "s"} with both dates
        </p>
      </div>
    </div>
  );
}

export function TopClients({ clients, selected, onSelect, title = "Top clients by commissioned value", idPrefix = "commissioned" }) {
  const max = Math.max(...clients.map((c) => c.value), 1);
  return (
    <div data-testid={`${idPrefix}-top-clients`} className="bg-white rounded-xl border border-stone-200 p-4">
      <PanelTitle>{title}</PanelTitle>
      <ul className="space-y-1">
        {clients.map((c) => {
          const k = c.key ?? c.company;
          const bunched = (c.variants || []).length > 1;
          return (
            <li key={k}>
              <button
                type="button"
                onClick={() => onSelect(k)}
                data-testid={`${idPrefix}-client-row-${c.company}`}
                title={
                  selected === k
                    ? "Clear client filter"
                    : bunched
                    ? `Drill into ${c.company} — includes ${c.variants.map((v) => v.name).join(", ")}`
                    : `Drill into ${c.company}`
                }
                className={`w-full text-left rounded px-1.5 py-1 hover:bg-stone-50 transition-colors ${selected && k !== selected ? "opacity-40" : ""}`}
              >
                <div className="flex items-center justify-between text-xs mb-0.5">
                  <span className="font-medium text-slate-800 truncate max-w-[65%]">
                    {c.company}
                    {bunched && (
                      <span className="ml-1.5 text-[9px] font-mono text-stone-400">
                        ×{c.variants.length} names
                      </span>
                    )}
                  </span>
                  <span className="font-mono tabular-nums text-stone-500">
                    {fmtGbpShort(c.value)} · {c.count}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-stone-100">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#1B4F8A] to-[#3EA8DC]"
                    style={{ width: `${Math.max((c.value / max) * 100, 2)}%` }}
                  />
                </div>
              </button>
            </li>
          );
        })}
        {clients.length === 0 && <li className="text-xs text-stone-400 px-1.5">No commissioned value in this view.</li>}
      </ul>
      <p className="text-[10px] text-stone-400 mt-2 px-1.5">click a client to drill down</p>
    </div>
  );
}

export function MonthlyTrend({ monthly, title = "Commissioned per month · last 12 months", color = "#0E9F6E", idPrefix = "commissioned" }) {
  const data = monthly.map((m) => ({ ...m, label: fmtMonth(m.month) }));
  return (
    <div data-testid={`${idPrefix}-monthly-trend`} className="bg-white rounded-xl border border-stone-200 p-4">
      <PanelTitle>{title}</PanelTitle>
      <div className="h-36">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 4, left: -26, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} />
            <RechartsTooltip
              formatter={(v, _n, p) => [`${v} project${v === 1 ? "" : "s"} · ${fmtGbpShort(p.payload.value)}`, fmtMonth(p.payload.month)]}
              labelFormatter={() => ""}
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
            />
            <Bar dataKey="count" fill={color} radius={[3, 3, 0, 0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
